import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';

import { AnnotationContainerComponent } from '../annotation-container/annotation-container.component';
import { AnnotationService } from '../annotation-container/annotation.service';
import { CompassComponent } from '../compass/compass.component';
import { HeaderComponent } from '../header/header.component';
import { NavigationControlsComponent } from '../navigation-controls/navigation-controls.component';
import { FeaturesService } from '../services/features.service';
import { InputHandlerService } from '../services/input-handler.service';
import { ViewerService } from '../services/viewer.service';
import { SceneStateService } from '../services/scene-state.service';
import { TerrainService } from '../services/terrain.service';
import { ToolsService } from '../services/tools.service';
import { ProfileWindowComponent } from '../profile-window/profile-window.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { onViewerEvents } from '../services/event-dispatcher-bridge';

@Component({
  selector: 'app-viewer',
  imports: [
    CommonModule,
    CompassComponent,
    ProfileWindowComponent,
    AnnotationContainerComponent,
    NavigationControlsComponent,
    SidebarComponent,
    HeaderComponent,
  ],
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewerComponent implements AfterViewInit, OnDestroy {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);
  protected annotationService = inject(AnnotationService);
  viewerService = inject(ViewerService);
  private terrain = inject(TerrainService);
  private tools = inject(ToolsService);

  // Bootstrap signal-based services (inject to trigger initialization)
  private features = inject(FeaturesService);
  private sceneState = inject(SceneStateService);
  private inputHandler = inject(InputHandlerService);
  viewer = signal<unknown | null>(null);

  showProfileWindow = signal(false);
  currentProfileId?: string;
  activeProfile: unknown = null;

  // Terrain state
  terrainLoading = false;
  terrainLoaded = false;
  terrainVisible = false;
  terrainError: string | null = null;
  terrainType: '3dtiles' | 'quantized' | null = null;

  // Quantized mesh terrain state
  quantizedTerrainLoading = false;
  quantizedTerrainLoaded = false;
  quantizedTerrainVisible = false;
  quantizedTerrainError: string | null = null;

  ngAfterViewInit(): void {
    const container = this.host?.nativeElement.querySelector('#renderArea') as HTMLElement;
    if (!container) return;

    this.viewerService
      .initViewer(container)
      .then(viewer => {
        this.viewer.set(viewer);
        setTimeout(() => {
          this.initializeAnnotations();
        }, 2000);
      })
      .catch(error => {
        console.error('Failed to initialize viewer:', error);
      });
  }

  private initializeAnnotations(): void {
    if (!this.viewer()) {
      return;
    }

    const viewer = this.viewer() as any;

    // Initialize the annotation service with the viewer
    this.annotationService.initialize(viewer, () => {});

    // Set up update loop integration with automatic cleanup on destroy
    if (viewer && viewer.addEventListener) {
      onViewerEvents(
        viewer,
        {
          update: () => {
            try {
              this.annotationService.updatePositions(viewer);
            } catch {
              // Silently handle position update errors
            }
          },
        },
        this.destroyRef,
      );
    }
  }

  ngOnDestroy(): void {
    this.viewerService.disposeViewer();
  }

  showProfile2D(id?: string): void {
    this.currentProfileId = id;
    // Call the tools service to set up the profile data
    this.tools.showProfile2D(id);
    // Get the active profile data
    this.activeProfile = this.tools.getActiveProfile();
    this.showProfileWindow.set(true);
  }
  /**
   * Load Swiss terrain 3D tiles
   */
  async loadTerrain(): Promise<void> {
    if (!this.viewer() || this.terrainLoaded) {
      return;
    }

    // Disable quantized mesh if loaded
    if (this.quantizedTerrainLoaded) {
      this.terrain.setQuantizedTerrainEnabled(false);
      this.quantizedTerrainVisible = false;
    }

    this.terrainLoading = true;
    this.terrainError = null;

    try {
      await this.terrain.loadSwissTerrain();
      this.terrainLoaded = true;
      this.terrainVisible = true;
      this.terrainType = '3dtiles';
    } catch (error) {
      console.error('Error loading terrain:', error);
      this.terrainError = 'Failed to load terrain. Please try again.';
      this.terrainLoaded = false;
      this.terrainVisible = false;
    } finally {
      this.terrainLoading = false;
    }
  }

  /**
   * Toggle terrain visibility
   */
  toggleTerrain(): void {
    if (!this.viewer() || !this.terrainLoaded) {
      return;
    }

    this.terrain.toggleTerrain();
    this.terrainVisible = this.terrain.terrainEnabled();
  }

  /**
   * Load quantized mesh terrain
   */
  async loadQuantizedMeshTerrain(): Promise<void> {
    if (!this.viewer() || this.quantizedTerrainLoaded) {
      return;
    }

    // Disable 3D tiles if loaded
    if (this.terrainLoaded) {
      this.terrain.setTerrainEnabled(false);
      this.terrainVisible = false;
    }

    this.quantizedTerrainLoading = true;
    this.quantizedTerrainError = null;

    try {
      await this.terrain.loadQuantizedMeshTerrain();
      this.quantizedTerrainLoaded = true;
      this.quantizedTerrainVisible = true;
      this.terrainType = 'quantized';
      console.log('Quantized mesh terrain loaded successfully');

      // Start stats update interval
    } catch (error) {
      console.error('Error loading quantized mesh terrain:', error);
      this.quantizedTerrainError = 'Failed to load terrain. Please try again.';
      this.quantizedTerrainLoaded = false;
      this.quantizedTerrainVisible = false;
    } finally {
      this.quantizedTerrainLoading = false;
    }
  }

  /**
   * Toggle quantized mesh terrain visibility
   */
  toggleQuantizedTerrain(): void {
    if (!this.viewer() || !this.quantizedTerrainLoaded) {
      return;
    }

    this.terrain.toggleQuantizedTerrain();
    this.quantizedTerrainVisible = this.terrain.quantizedTerrainEnabled();
  }

  /**
   * Switch between terrain types
   */
  async switchToTerrainType(type: '3dtiles' | 'quantized'): Promise<void> {
    if (type === '3dtiles') {
      // Disable quantized if active
      if (this.quantizedTerrainLoaded) {
        this.terrain.setQuantizedTerrainEnabled(false);
        this.quantizedTerrainVisible = false;
      }

      // Load or enable 3D tiles
      if (!this.terrainLoaded) {
        await this.loadTerrain();
      } else {
        this.terrain.setTerrainEnabled(true);
        this.terrainVisible = true;
      }
      this.terrainType = '3dtiles';
    } else if (type === 'quantized') {
      // Disable 3D tiles if active
      if (this.terrainLoaded) {
        this.terrain.setTerrainEnabled(false);
        this.terrainVisible = false;
      }

      // Load or enable quantized mesh
      if (!this.quantizedTerrainLoaded) {
        await this.loadQuantizedMeshTerrain();
      } else {
        this.terrain.setQuantizedTerrainEnabled(true);
        this.quantizedTerrainVisible = true;
      }
      this.terrainType = 'quantized';
    }
  }

  onProfile2DClick(event: unknown, id: string): void {
    if (event && typeof (event as any).stopPropagation === 'function') {
      (event as any).stopPropagation();
    }
    this.showProfile2D(id);
  }

  onProfileWindowClosed(): void {
    this.showProfileWindow.set(false);
    this.currentProfileId = undefined;
    this.activeProfile = null;
  }
}
